import { useState, useRef } from "react";
import { Product } from "@/mocks/products";

interface ProductCardProps {
  product: Product;
  onOpenDetail?: (product: Product) => void;
  editMode?: boolean;
  imageOverride?: string;
  onImageChange?: (productId: string, url: string) => void;
  onImageReset?: (productId: string) => void;
}

const badgeColors: Record<string, string> = {
  NEW: "bg-emerald-100 text-emerald-700",
  SALE: "bg-rose-100 text-rose-600",
  BEST: "bg-amber-100 text-amber-700",
};

const KAKAO_URL = "https://open.kakao.com/o/srLtta1b";

const ProductCard = ({
  product,
  onOpenDetail,
  editMode = false,
  imageOverride,
  onImageChange,
  onImageReset,
}: ProductCardProps) => {
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentImage = imageOverride ?? product.image;
  const isCustom = !!imageOverride;

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  const handleInquiry = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(KAKAO_URL, "_blank", "noopener,noreferrer");
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUrlDraft(currentImage);
    setPreviewError(false);
    setShowUrlInput(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    const trimmed = urlDraft.trim();
    if (trimmed) {
      onImageChange?.(product.id, trimmed);
    }
    setShowUrlInput(false);
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onImageReset?.(product.id);
    setShowUrlInput(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowUrlInput(false);
  };

  return (
    <div
      className="group bg-white rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1"
      onClick={() => !editMode && onOpenDetail?.(product)}
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-stone-50" style={{ aspectRatio: "6/7" }}>
        <img
          src={currentImage}
          alt={product.name}
          title={`${product.name} - 도어준`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            (e.target as HTMLImageElement).src = product.image;
          }}
        />

        {product.badge && (
          <span className={`absolute top-3 left-3 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full ${badgeColors[product.badge] || "bg-stone-100 text-stone-600"}`}>
            {product.badge}
          </span>
        )}
        {discount && (
          <span className="absolute top-3 right-3 text-[10px] font-bold bg-rose-500 text-white px-2 py-1 rounded-full">
            -{discount}%
          </span>
        )}

        {/* 편집 모드 오버레이 */}
        {editMode && !showUrlInput && (
          <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={handleEditClick}
              className="flex items-center gap-1.5 bg-white text-stone-900 text-xs font-bold px-4 py-2 rounded-full hover:bg-stone-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-image-edit-line text-sm" />
              이미지 URL 변경
            </button>
            {isCustom && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 bg-white/80 text-stone-500 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line text-xs" />
                원본으로 복원
              </button>
            )}
          </div>
        )}

        {/* URL 입력 팝업 */}
        {editMode && showUrlInput && (
          <div
            className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center p-3 gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-white text-xs font-bold mb-1">이미지 URL 입력</p>
            <input
              ref={inputRef}
              type="text"
              value={urlDraft}
              onChange={(e) => { setUrlDraft(e.target.value); setPreviewError(false); }}
              placeholder="https://..."
              className="w-full text-xs px-3 py-2 rounded-lg border border-stone-300 outline-none focus:border-white bg-white text-stone-800"
              onClick={(e) => e.stopPropagation()}
            />
            {previewError && (
              <p className="text-rose-300 text-[10px]">이미지를 불러올 수 없어요</p>
            )}
            <div className="flex gap-2 w-full">
              <button
                onClick={handleConfirm}
                className="flex-1 bg-white text-stone-900 text-xs font-bold py-1.5 rounded-lg hover:bg-stone-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                적용
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 bg-white/20 text-white text-xs font-semibold py-1.5 rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
            </div>
          </div>
        )}

        {/* 커스텀 이미지 표시 뱃지 */}
        {isCustom && !editMode && (
          <span className="absolute bottom-2 right-2 bg-emerald-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
            내 이미지
          </span>
        )}

        {/* 일반 모드 견적 오버레이 */}
        {!editMode && (
          <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <button
              onClick={handleInquiry}
              className="w-full bg-stone-900 text-white text-xs font-semibold tracking-wider py-3 hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5"
            >
              <i className="ri-kakao-talk-fill" />
              견적 문의하기
            </button>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <p className="text-[10px] font-medium text-stone-400 tracking-widest uppercase mb-1">{product.category}</p>
        <h3 className="text-sm font-semibold text-stone-800 mb-1.5 leading-snug">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-stone-500 leading-relaxed mb-2 line-clamp-2">{product.description}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <i className="ri-chat-smile-2-line text-sm" style={{ color: "#b8860b" }} />
            <span className="text-sm font-bold text-stone-700">카카오톡 문의</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-fill text-amber-400 text-xs" />
            </div>
            <span className="text-xs text-stone-500">{product.rating}</span>
            <span className="text-xs text-stone-300">({product.reviews})</span>
          </div>
        </div>
        {!editMode && (
          <button
            onClick={handleInquiry}
            className="mt-3 w-full border border-stone-200 text-stone-700 text-xs font-semibold py-2 rounded-lg hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            무료 견적 받기
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
